import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Table from "@mui/joy/Table";
import Link from "@mui/joy/Link";
import Chip from "@mui/joy/Chip";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import { AnalyticsFilters, AnalyticsFiltersState } from "../components/AnalyticsFilters";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    ScrollTable,
    WatchSignals,
} from "../components/AnalyticsShared";
import { useAsyncBlock } from "../hooks";
import { bidsPriceSpreadApi } from "../../bidsApi";
import { BidPriceSpreadInfo } from "../../../../models/bidAnalytics";
import { formatInt, formatMoneyKopecks, gameName, toNum } from "../format";
import { Game } from "../../../../models/games";

type Props = {
    isActive: boolean;
    filters: AnalyticsFiltersState;
    onFiltersChange: (next: AnalyticsFiltersState) => void;
    games: Game[];
    gamesLoading: boolean;
};

function spreadColor(spread: number, hasSell: boolean): "danger" | "success" | "warning" | "neutral" {
    if (!hasSell) {
        return "warning";
    }
    if (spread < 0) {
        return "danger";
    }
    return "success";
}

export function PriceSpreadTab(props: Props) {
    const { isActive, filters, onFiltersChange, games, gamesLoading } = props;

    const [state, refresh] = useAsyncBlock<BidPriceSpreadInfo[]>(
        () =>
            bidsPriceSpreadApi({
                take: filters.take,
                gameId: filters.gameId,
            }),
        isActive,
        [filters.take, filters.gameId]
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <AnalyticsFilters
                value={filters}
                onChange={onFiltersChange}
                onRefresh={refresh}
                loading={state.loading}
                games={games}
                gamesLoading={gamesLoading}
                showPeriod={false}
                showTake
            />

            <AnalyticsCard
                title="Где заявки не сходятся с продажами"
                subtitle="Находит скины, где покупатели и продавцы «не встречаются» по цене."
                snapshotLabel="now"
                howToRead={
                    <List size="sm" marker="disc">
                        <ListItem>
                            <Typography level="body-sm">
                                Разница цен &lt; 0 — заявка ниже минимальной продажи (сделка сама не
                                случится, пока цены не сблизятся)
                            </Typography>
                        </ListItem>
                        <ListItem>
                            <Typography level="body-sm">
                                Разница цен ≥ 0 — заявка покрывает или выше мин. продажи (есть потенциал
                                сделки)
                            </Typography>
                        </ListItem>
                        <ListItem>
                            <Typography level="body-sm">
                                Нет оффера — продавать нечего, спрос «в воздухе»
                            </Typography>
                        </ListItem>
                    </List>
                }
                watchFor={
                    <WatchSignals
                        items={[
                            {
                                signal: "Разница цен (заявка − мин. продажа)",
                                ifGrows: "заявка покрывает мин. продажу — есть потенциал автосделки.",
                                ifFalls: "заявка ниже продажи — сделка сама не случится, пока цены не сблизятся; большой остаток = зависший спрос.",
                            },
                            {
                                signal: "Доля скинов с оффером на продажу",
                                ifGrows: "оффер есть и разница цен положительная — позиция может исполниться.",
                                ifFalls: "покупать нечего — спрос «в воздухе»; нужно привлечь продавцов или снять мёртвые заявки.",
                            },
                            {
                                signal: "Остаток штук при отрицательной разнице цен",
                                growsIsGood: false,
                                ifGrows: "накапливается неудовлетворённый объём — приоритет для ручной работы с ценой/офферами.",
                                ifFalls: "остаток сходит при сближении цен — рынок сходится.",
                            },
                        ]}
                    />
                }
            >
                {state.loading && <BlockLoading />}
                {!state.loading && state.error && <BlockError message={state.error} onRetry={refresh} />}
                {!state.loading && !state.error && (!state.data || state.data.length === 0) && (
                    <EmptyState hint="Сейчас нет активных позиций для сравнения. Смените игру или обновите." />
                )}
                {!state.loading && !state.error && state.data && state.data.length > 0 && (
                    <ScrollTable>
                        <Table size="sm" stickyHeader sx={{ minWidth: 960 }}>
                            <thead>
                                <tr>
                                    <th>Скин</th>
                                    <th>Игра</th>
                                    <th>Макс. заявка</th>
                                    <th>Мин. продажа</th>
                                    <th>Разница цен</th>
                                    <th>Остаток</th>
                                    <th>Активных заявок</th>
                                    <th>Оффер</th>
                                </tr>
                            </thead>
                            <tbody>
                                {state.data.map((row) => {
                                    const spread = toNum(row.spread);
                                    const color = spreadColor(spread, row.hasSellOffer);
                                    return (
                                        <tr key={`${row.marketSkinId}-${row.hashName}`}>
                                            <td>
                                                <Link
                                                    level="body-sm"
                                                    href={`/app/bids?hashName=${encodeURIComponent(row.hashName)}#list`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {row.hashName || row.marketSkinId}
                                                </Link>
                                            </td>
                                            <td>{gameName(row.gameId)}</td>
                                            <td>{formatMoneyKopecks(row.maxBidPrice)}</td>
                                            <td>
                                                {row.hasSellOffer
                                                    ? formatMoneyKopecks(row.minSellPrice)
                                                    : "—"}
                                            </td>
                                            <td>
                                                <Chip size="sm" variant="soft" color={color}>
                                                    {row.hasSellOffer
                                                        ? formatMoneyKopecks(spread)
                                                        : "нет продажи"}
                                                </Chip>
                                            </td>
                                            <td>{formatInt(row.remainingQty)}</td>
                                            <td>{formatInt(row.activeBidsCount)}</td>
                                            <td>
                                                <Chip
                                                    size="sm"
                                                    variant="soft"
                                                    color={row.hasSellOffer ? "success" : "warning"}
                                                >
                                                    {row.hasSellOffer ? "Есть" : "Нет"}
                                                </Chip>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </ScrollTable>
                )}
            </AnalyticsCard>
        </Box>
    );
}
